import React, { useState } from "react";

import { Modal } from "../../../common/Modal";
import {
  UsersIcon,
  BuildingIcon,
  MapPinIcon,
} from "../../../../assets/ui/icons";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { SectionAddBar } from "./SectionAddBar";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { MembershipVerifyNotice } from "./MembershipVerifyNotice";
import { DetailCard } from "./DetailCard";
import { RelationFormModal } from "./RelationFormModal";
import { VerificationModal } from "./VerificationModal";
import { useRelationSection } from "./useRelationSection";
import {
  relationKey,
  relationRowValues,
  relationRows,
  relationStatus,
} from "./relationFields";

/* Sections 11 and 12 — Related Entities and Business Relation. */
const ROW_ICONS = {
  relation_type: UsersIcon,
  company: BuildingIcon,
  state: MapPinIcon,
  country: MapPinIcon,
};

export const RelationSection = ({
  spec,
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const { rows, remove, refresh } = useRelationSection(spec);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const openForm = (record) => {
    setEditing(record || null);
    setChosen(null);
    setFormOpen(true);
  };

  const close = () => {
    setFormOpen(false);
    setEditing(null);
    setChosen(null);
  };

  const cardRows = (record) => {
    const values = relationRowValues(record);
    return relationRows(spec).map((row) => ({
      key: row.key,
      label: row.label,
      icon: ROW_ICONS[row.key],
      value: values[row.key],
    }));
  };

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar onBack={onPrev} onSkip={onSkip} onContinue={onNext} />
      }
    >
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        {spec.title}
      </h2>

      {/* Members only — see MembershipVerifyNotice. The offer says the
          verification is free WITH YOUR MEMBERSHIP, so it is not shown to
          anyone who does not have one. */}
      <MembershipVerifyNotice onUpload={() => setVerifyOpen(true)} />

      <SectionAddBar
        className="mt-4"
        prompt={spec.addPrompt}
        label={spec.addLabel}
        onAdd={() => openForm(null)}
      />

      {rows.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {rows.map((record) => (
            <DetailCard
              key={relationKey(record, spec)}
              status={relationStatus(record)}
              rows={cardRows(record)}
              onEdit={() => openForm(record)}
              onDelete={() => remove(record)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <Modal modalHandle={close} noClose bare>
          <RelationFormModal
            spec={spec}
            editing={editing}
            chosen={chosen}
            onChoose={setChosen}
            onClose={close}
            onSaved={() => {
              close();
              refresh();
            }}
          />
        </Modal>
      )}

      {verifyOpen && (
        <Modal modalHandle={() => setVerifyOpen(false)} noClose bare>
          <VerificationModal
            spec={{
              uploadPath: uploadFilePath?.[spec.uploadPath],
              category: spec.category,
            }}
            onClose={() => setVerifyOpen(false)}
            onDone={() => {
              setVerifyOpen(false);
              refresh();
            }}
          />
        </Modal>
      )}
    </EditSectionLayout>
  );
};

export default RelationSection;
